import {
  Box,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Textarea,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { RiSendPlaneLine } from 'react-icons/ri';
import {
  DtType,
  ProposalType,
  RequestsDocument,
  useCreateProjectProposalMutation,
} from '../../generated/graphql';
import { HelivanBackButton } from '../helivan/HelivanBackButton';
import { HelivanSelector } from '../helivan/HelivanSelector';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const RequestModal = ({
  isOpen,
  onClose,
  prefill,
}: {
  isOpen: boolean;
  onClose: () => void;
  prefill?: ProposalType;
}) => {
  const [createProposal, { loading }] = useCreateProjectProposalMutation();
  const [type, setType] = useState<ProposalType | undefined>(
    prefill ?? undefined
  );
  const [dtType, setDtType] = useState<DtType>();
  const [title, setTitle] = useState<string>();
  const [notes, setNotes] = useState<string>();
  const [step, setStep] = useState<number>(0);
  const [personaType, setPersonaType] = useState<string>();

  const customOnClose = () => {
    setType(undefined);
    setTitle(undefined);
    setDtType(undefined);
    setNotes(undefined);
    setPersonaType(undefined);
    setStep(0);
    onClose();
  };

  const canContinueStep0 =
    type &&
    !(
      (personaType === 'add_to_collection' || type === ProposalType.Project) &&
      !dtType
    ) &&
    !(type === ProposalType.DigitalTwin && !personaType);

  const canSubmitStep1 = notes && title && type;

  const handleSubmit = () => {
    if (!canSubmitStep1) return;

    createProposal({
      variables: {
        input: {
          type:
            personaType === 'new_collection' ? ProposalType.Collection : type,
          dtType:
            type === ProposalType.Project || personaType === 'add_to_collection'
              ? dtType
              : undefined,
          notes,
          title,
        },
      },
      onCompleted: () => {
        customOnClose();
      },
      refetchQueries: [{ query: RequestsDocument }],
    });
  };

  const contentSteps = [
    {
      content: (
        <Stack spacing={5} width="full">
          <HelivanSelector
            options={[
              { value: 'Exporable', key: ProposalType.Project },
              { value: 'Persona', key: ProposalType.DigitalTwin },
            ]}
            onSelect={setType}
            question="Select request type"
            selectedOption={type}
          />

          {type === ProposalType.DigitalTwin && (
            <HelivanSelector
              onSelect={setPersonaType}
              options={[
                { key: 'new_collection', value: 'Create new set' },
                {
                  key: 'add_to_collection',
                  value: 'Add to existing set (eg Congresspersons, etc)',
                },
              ]}
              question="Where should this agent go?"
              selectedOption={personaType ?? null}
            />
          )}
          {(type === ProposalType.Project ||
            personaType === 'add_to_collection') && (
            <HelivanSelector
              onSelect={setDtType}
              options={[
                { key: DtType.Congressperson, value: 'Congressperson' },
                {
                  key: DtType.EarlyStageVcs,
                  value: 'VC',
                },
              ]}
              question="Who are you asking your question to?"
              selectedOption={dtType ?? null}
            />
          )}
        </Stack>
      ),
    },
    {
      content: (
        <Stack spacing={5} width="full">
          <Box>
            <Text
              fontSize="13px"
              fontWeight={600}
              color="#475569"
              mb={2}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              {type === ProposalType.Project
                ? 'What would you want to be added?'
                : type === ProposalType.DigitalTwin
                  ? personaType === 'add_to_collection' && dtType
                    ? `Who do you want to be added to ${dtType === DtType.Congressperson ? 'Congresspersons' : dtType === DtType.EarlyStageVcs ? 'Venture Capitalists' : 'this collection'}?`
                    : 'What group of Personas are you requesting?'
                  : 'What would you want to be added?'}
            </Text>
            <Input
              size="lg"
              bg="#f8fafc"
              border="2px solid"
              borderColor="#e2e8f0"
              borderRadius="14px"
              fontSize="15px"
              color="#1a1a2e"
              _placeholder={{ color: '#94a3b8' }}
              _hover={{ borderColor: '#cbd5e1' }}
              _focus={{
                borderColor: '#00477D',
                boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                bg: 'white',
              }}
              transition="all 0.2s"
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              value={title}
              placeholder="Enter your request..."
            />
          </Box>
          <Box>
            <Text
              fontSize="13px"
              fontWeight={600}
              color="#475569"
              mb={2}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              Why do you want this to be added?
            </Text>
            <Textarea
              size="lg"
              bg="#f8fafc"
              border="2px solid"
              borderColor="#e2e8f0"
              borderRadius="14px"
              fontSize="15px"
              color="#1a1a2e"
              _placeholder={{ color: '#94a3b8' }}
              _hover={{ borderColor: '#cbd5e1' }}
              _focus={{
                borderColor: '#00477D',
                boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                bg: 'white',
              }}
              transition="all 0.2s"
              rows={4}
              resize="none"
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              value={notes}
              placeholder="Explain your reasoning..."
            />
          </Box>
        </Stack>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={customOnClose} size="lg" isCentered motionPreset="slideInBottom">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={4}
        overflow="visible"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius="28px"
          bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        >
          {/* Header with gradient accent */}
          <Box position="relative" overflow="hidden">
            {/* Gradient bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, #00477D, #486884, #00477D)"
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />

            <Flex
              px={6}
              pt={6}
              pb={4}
              align="center"
              gap={4}
            >
              <HelivanBackButton
                onClick={
                  step === 0
                    ? customOnClose
                    : () => {
                        setStep(0);
                      }
                }
              />

              <Flex flex={1} align="center" gap={4}>
                {/* Icon with gradient background */}
                <Flex
                  w="56px"
                  h="56px"
                  borderRadius="16px"
                  bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                  align="center"
                  justify="center"
                  boxShadow="0 4px 14px rgba(0,71,125,0.3)"
                >
                  <RiSendPlaneLine size={28} color="white" />
                </Flex>

                <Box>
                  <Text
                    fontSize="20px"
                    fontWeight={700}
                    color="#1a1a2e"
                    letterSpacing="-0.02em"
                  >
                    Make A Request
                  </Text>
                  <Text
                    fontSize="14px"
                    color="#64748b"
                    mt={0.5}
                  >
                    {step === 0 ? 'Choose what you want to request' : 'Tell us more about your request'}
                  </Text>
                </Box>
              </Flex>

              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody px={6} py={5}>
            {contentSteps[step].content}
          </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={5}
            bg="#f8fafc"
            borderTop="1px solid"
            borderColor="#e2e8f0"
            justify="space-between"
            align="center"
          >
            <HStack spacing="6px">
              <Box
                borderRadius="8px"
                height="12px"
                width={step === 0 ? '20px' : '48px'}
                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
              />
              <Box
                borderRadius="8px"
                height="12px"
                width={step === 0 ? '48px' : '20px'}
                bg="#e2e8f0"
              />
            </HStack>

            <Box
              as="button"
              px={6}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="white"
              bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
              cursor={
                (step === 0 && !canContinueStep0) ||
                (step === 1 && (!canSubmitStep1 || loading))
                  ? 'not-allowed'
                  : 'pointer'
              }
              opacity={
                (step === 0 && !canContinueStep0) ||
                (step === 1 && (!canSubmitStep1 || loading))
                  ? 0.6
                  : 1
              }
              transition="all 0.2s"
              boxShadow="0 2px 8px rgba(0,71,125,0.3)"
              _hover={{
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
              }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={
                step === 0
                  ? canContinueStep0
                    ? () => setStep(1)
                    : undefined
                  : handleSubmit
              }
            >
              {loading ? <Spinner size="xs" /> : step === 0 ? 'Continue' : 'Submit'}
            </Box>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
